import React, { FC } from "react";
import classNames from "classnames";
import { IconDownloadGlyph, IconFileTextGlyph, IconImageGlyph } from "./icons";

/** Design §04 tile geometry. */

interface ImageTileProps {
  src?: string;
  caption: string;
  /**
   * File format, rendered in Black weight after the caption and never
   * truncated. On a thumbnail the format is identifying information — which
   * PNG of the three you are downloading — so it cannot be the part that fades.
   */
  captionExt?: string;
  onDownload?: () => void;
  /** `.tif` and friends: no browser-renderable preview, show a glyph plate. */
  glyphOnly?: boolean;
}

/**
 * Fixed 16/9 + `object-fit: cover`. The old grid used `height:auto` with
 * `object-fit: contain`, so a library that mixes 9x16 cutdowns with 16x9
 * masters rendered as a staircase of ragged tiles.
 *
 * The download control sits INSIDE the image on a `--scrim` plate rather than
 * floating at a hand-tuned `top-6 right-2` in `#B0B0B0` on white, where it was
 * effectively invisible over photography.
 */
export const ImageTile: FC<ImageTileProps> = ({
  src,
  caption,
  captionExt,
  onDownload,
  glyphOnly,
}) => {
  const fullCaption = captionExt ? `${caption} · ${captionExt}` : caption;
  return (
    <div className="min-w-0">
      <div
        className={classNames(
          "relative rounded-lg overflow-hidden flex items-center justify-center",
          glyphOnly ? "bg-tint text-accent" : "bg-track text-muted",
        )}
        style={{ aspectRatio: "16 / 9" }}
      >
        {glyphOnly ? (
          <IconImageGlyph size={26} strokeWidth={1.6} />
        ) : (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        {/*
          A 24px `--scrim` plate vanishes into a bright frame — a white-on-white
          product shot leaves the chip readable only where it overlaps the one
          dark pixel. The same token faded up from the bottom edge gives it a
          floor. This is the one fade the page allows: it carries no text of its
          own, and the chip on top stays the solid plate the system asks for.
          The glyph plate is a flat `--tint` block whose chip is light, so it
          gets nothing.
        */}
        {onDownload && !glyphOnly && (
          <span
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{ height: 36, background: "linear-gradient(180deg, transparent, var(--scrim))" }}
          />
        )}
        {onDownload && (
          <button
            type="button"
            aria-label={`Download ${fullCaption}`}
            title={`Download ${fullCaption}`}
            onClick={onDownload}
            className={classNames(
              "absolute right-1.5 bottom-1.5 w-6 h-6 rounded-lg inline-flex items-center justify-center transition-opacity hover:opacity-85",
              glyphOnly ? "bg-surface text-text-1" : "text-white",
            )}
            style={glyphOnly ? { boxShadow: "var(--sh-1)" } : { background: "var(--scrim)" }}
          >
            <IconDownloadGlyph size={13} strokeWidth={2} />
          </button>
        )}
      </div>
      {/*
        Two spans rather than one truncating line: the ellipsis has to eat the
        caption, never the format. `shrink-0` is what pins the format in place
        when the caption is longer than the tile.
      */}
      <div
        className="flex text-text-2"
        style={{ fontSize: 11, lineHeight: 1.3, marginTop: 6 }}
        title={fullCaption}
      >
        <span className="truncate min-w-0">{caption}</span>
        {captionExt && (
          // fontFamily inline, not `font-CircularStdBlack`: tailwind.config.js has
          // no fontFamily key, so that class emits NO CSS and the format would
          // render at body weight — which is the one thing correction 9 forbids,
          // since the format is now identifying information.
          <span className="shrink-0" style={{ fontFamily: "CircularStdBlack" }}>{` · ${captionExt}`}</span>
        )}
      </div>
    </div>
  );
};

interface FileRowProps {
  name: string;
  /** "SubRip · 4 KB" — the size half is dropped when it isn't known. */
  meta?: string;
  onDownload?: () => void;
}

/** Replaces the 210px `--tint` blocks: a transcript is a file, not a poster. */
export const FileRow: FC<FileRowProps> = ({ name, meta, onDownload }) => (
  <div
    className="flex items-center gap-[11px] border border-border-token rounded-xl px-3 min-w-0"
    style={{ height: 52 }}
  >
    <span
      className="shrink-0 rounded-lg bg-tint text-accent-text inline-flex items-center justify-center"
      style={{ width: 30, height: 30 }}
    >
      <IconFileTextGlyph size={15} strokeWidth={1.7} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block truncate text-text-1" style={{ fontSize: 13, lineHeight: 1.2 }} title={name}>
        {name}
      </span>
      {meta && (
        <span className="block truncate text-muted" style={{ fontSize: 11, marginTop: 3 }}>
          {meta}
        </span>
      )}
    </span>
    {onDownload && (
      <button
        type="button"
        aria-label={`Download ${name}`}
        title={`Download ${name}`}
        onClick={onDownload}
        className="shrink-0 rounded-lg bg-surface-2 text-text-1 inline-flex items-center justify-center hover:bg-track transition-colors"
        style={{ width: 28, height: 28 }}
      >
        <IconDownloadGlyph size={14} strokeWidth={2} />
      </button>
    )}
  </div>
);

interface StoryTileProps {
  cover?: string;
  title: string;
  subtitle?: string;
  videoCount: number;
  onClick?: () => void;
}

/**
 * Page-local, so `/videos/embeds` keeps its own card untouched — and so the
 * story Edit/Delete kebab does not appear on a video page. On this page those
 * two controls were already inert: `StoriesList` passes neither `onDelete` nor
 * `refetch`, and the shared card calls both optionally, so Delete opened a
 * confirm dialog that deleted nothing and Edit left the panel stale.
 */
export const StoryTile: FC<StoryTileProps> = ({
  cover,
  title,
  subtitle,
  videoCount,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left border border-border-token rounded-xl p-2.5 bg-surface hover:border-accent transition-colors min-w-0"
  >
    <div
      className="relative rounded-lg overflow-hidden bg-track flex items-center justify-center"
      style={{ height: 96 }}
    >
      {cover && (
        <img
          src={cover}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      )}
      <span
        className="absolute right-2 bottom-2 inline-flex items-center h-5 px-2 rounded-full text-white"
        style={{ background: "var(--scrim)", fontSize: 10.5 }}
      >
        {videoCount} {videoCount === 1 ? "video" : "videos"}
      </span>
    </div>
    <div
      className="truncate text-text-1"
      style={{ fontSize: 13, lineHeight: 1.3, marginTop: 9 }}
      title={title}
    >
      {title}
    </div>
    {subtitle && (
      <div className="truncate text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
        {subtitle}
      </div>
    )}
  </button>
);
